import { DndContext, DragOverlay, PointerSensor, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { DEFAULT_PROJECT_DOCUMENT_TREE_NESTED_INDENT_PX } from "@sudowrite/shared-chat";
import { debounce } from "lodash";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useStableCallback } from "src/hooks/useStableCallback";
import { cn } from "src/lib/cn";
import styled from "styled-components/macro";
import { sendAppEvent } from "../../../../Utils";
import { useProjectStore } from "../../../../stores/projectStore";
import { SortableDocument } from "../../DocumentListItem";
import { DroppableFolder } from "../FolderListItem";
import { customCollision } from "./customCollision";
import {
  applyBatchTreeMoves,
  buildBatchFolderMovePlan,
  buildBatchProjectUpdate,
  buildBatchSeparatorMovePlan,
  buildVisibleNodeIds,
  getBatchMoveItemCount,
  getNextSelection,
  isBatchFolderDropTarget,
  isBatchDropDisabled,
  pruneSelectionToVisible,
  validateBatchFolderDrop,
} from "../treeMultiSelectUtils";
import { getProjectTreeSyncKey } from "./getProjectTreeSyncKey";
import { useKeyboardReorder } from "./useKeyboardReorder";
import { shouldPreserveProjectTreeSelection } from "../projectTreeSelectionClickAway";
import { useProjectTreeSelectionStore } from "../../../../stores/projectTreeSelectionStore";

/** Stable sensor options — a new options object each render rebuilds dnd-kit activator handlers. */
const POINTER_SENSOR_OPTIONS = {
  activationConstraint: {
    distance: 8,
  },
};

const DND_MODIFIERS = [restrictToVerticalAxis];

const DroppableArea = ({ parentId, draggingNode, nestingLevel, displayOrder, dragPayloadIds, nodesById }) => {
  const project = useProjectStore((state) => state.project);

  const tooManyLevels = draggingNode && draggingNode.type === "folder" && nestingLevel >= 19;
  const isDecendant = draggingNode && draggingNode.type === "folder" && project.getDirectAncestors(parentId).includes(draggingNode.id);
  const batchDisableDroppable =
    dragPayloadIds.length > 1 &&
    isBatchDropDisabled({
      selectedIds: dragPayloadIds,
      destinationParentId: parentId,
      nestingLevel,
      nodesById,
    });
  const disableDroppable = tooManyLevels || isDecendant || batchDisableDroppable;

  const { attributes, setNodeRef, isOver } = useDroppable({
    id: `${parentId}:${displayOrder}`,
    data: { displayOrder, dropType: "separator", parentId },
    disabled: disableDroppable,
  });

  return (
    <div
      className={cn("absolute h-0.5 right-0 rounded-lg transition-all", isOver ? "bg-app-primary-cta translate-x-0" : "bg-transparent -translate-x-4")}
      // Rows now self-indent via the shared chrome's depth instead of the old
      // nested-ul margins, so the separator has to add the same per-level indent
      // to line up under the row it drops beside.
      style={{ left: 16 + nestingLevel * DEFAULT_PROJECT_DOCUMENT_TREE_NESTED_INDENT_PX }}
      ref={setNodeRef}
      {...attributes}
    />
  );
};

const FolderEndDroppableArea = ({ folderId, nestingLevel, displayOrder, isDragging, batchDragActive, dragPayloadIds, nodesById }) => {
  const batchDisableDroppable =
    dragPayloadIds.length > 1 &&
    isBatchDropDisabled({
      selectedIds: dragPayloadIds,
      destinationParentId: folderId,
      nestingLevel,
      nodesById,
    });
  const { attributes, setNodeRef, isOver } = useDroppable({
    id: `folder-end:${folderId}:${displayOrder}`,
    data: { displayOrder, dropType: "folder-end", parentId: folderId },
    disabled: (!isDragging && !batchDragActive) || batchDisableDroppable,
  });

  return (
    <div
      className={cn(
        "mr-1 mt-1 -mb-1 rounded-lg text-center items-center border-b transition-all flex pl-2",
        isOver ? "border-app-primary-cta" : "border-b-transparent",
        isDragging || batchDragActive ? "opacity-100" : "opacity-0 pointer-events-none",
      )}
      // `nestingLevel` here is the depth of the folder's children, so this lines
      // the bar up with the left edge of the rows it would append to. The old
      // 16px-per-level indent drifted from the rows once nested ul margins were
      // replaced by the shared chrome's depth.
      style={{
        marginLeft: `${nestingLevel * DEFAULT_PROJECT_DOCUMENT_TREE_NESTED_INDENT_PX}px`,
      }}
      ref={setNodeRef}
      {...attributes}
    />
  );
};

function firstDisplayOrder(nodes) {
  if (nodes.length === 0) {
    return 0;
  }
  return Math.min(...nodes.map((node) => node.displayOrder)) - 1;
}

const TreeDisplayFolder = ({
  node,
  folder,
  draggingNode,
  nestingLevel,
  displayOrder,
  inputRef,
  getDocument,
  getFolder,
  newTitle,
  setNewTitle,
  updateDocumentTitle,
  updateTitle,
  selectedNodeIds,
  onNodeSelect,
  collapsedFolderIds,
  keyboardExpandedFolderIds,
  clearKeyboardExpandedFolderId,
  onFolderCollapsedChange,
  batchDragActive,
  dragPayloadIds,
  nodesById,
  onKeyboardMove,
  ancestorSelected = false,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(folder?.collapsed ?? false);
  // A selected folder's visible contents render as selected too, so users know
  // the contents move with the folder.
  const isVisuallySelected = ancestorSelected || selectedNodeIds.includes(node.id);
  const isAriaSelected = selectedNodeIds.includes(node.id);

  useEffect(() => {
    if (keyboardExpandedFolderIds?.has(node.id)) return;
    setIsCollapsed(folder?.collapsed ?? false);
  }, [folder?.collapsed, folder?.id, keyboardExpandedFolderIds, node.id]);

  useEffect(() => {
    onFolderCollapsedChange(node.id, isCollapsed);
  }, [isCollapsed, node.id, onFolderCollapsedChange]);

  const showFolderEndDroppable = !isCollapsed && node.children && node.children.length > 0;

  useEffect(() => {
    if (keyboardExpandedFolderIds?.has(node.id)) {
      setIsCollapsed(false);
    }
  }, [folder?.collapsed, keyboardExpandedFolderIds, node.id]);

  const handleSingleClick = useMemo(() => {
    return () => {
      clearKeyboardExpandedFolderId?.(node.id);
      setIsCollapsed((previous) => !previous);
      node.toggleCollapsed();
    };
  }, [clearKeyboardExpandedFolderId, node]);

  // A debounced function that only fires once no calls
  // have come in for 10 ms
  // Prevents fast single clicks from triggering a double click
  const debouncedClick = useMemo(() => debounce(handleSingleClick, 10, { leading: false, trailing: true }), [handleSingleClick]);

  // Cancel debounced click on unmount
  useEffect(() => () => debouncedClick.cancel(), [debouncedClick]);

  return (
    <Fragment key={node.id}>
      <DroppableArea
        parentId={node.parentId}
        draggingNode={draggingNode}
        nestingLevel={nestingLevel}
        displayOrder={displayOrder}
        dragPayloadIds={dragPayloadIds}
        nodesById={nodesById}
      />
      <DroppableFolder
        folderId={node.id}
        toggleCollapsed={(event) => {
          if ((event?.detail ?? 0) > 1) return;
          debouncedClick();
        }}
        collapsed={isCollapsed}
        folder={folder}
        inputRef={inputRef}
        nestingLevel={nestingLevel}
        draggingNode={draggingNode}
        firstDisplayOrder={firstDisplayOrder(node.children)}
        isSelected={isVisuallySelected}
        ariaSelected={isAriaSelected}
        onSelect={onNodeSelect}
        dragPayloadIds={dragPayloadIds}
        nodesById={nodesById}
        onKeyboardMove={onKeyboardMove}
      />
      {node.children && !isCollapsed && (
        <>
          <TreeDisplay
            tree={node.children}
            getDocument={getDocument}
            getFolder={getFolder}
            parentId={node.id}
            newTitle={newTitle}
            setNewTitle={setNewTitle}
            updateDocumentTitle={updateDocumentTitle}
            inputRef={inputRef}
            updateTitle={updateTitle}
            nestingLevel={nestingLevel + 1}
            draggingNode={draggingNode}
            selectedNodeIds={selectedNodeIds}
            onNodeSelect={onNodeSelect}
            collapsedFolderIds={collapsedFolderIds}
            keyboardExpandedFolderIds={keyboardExpandedFolderIds}
            clearKeyboardExpandedFolderId={clearKeyboardExpandedFolderId}
            onFolderCollapsedChange={onFolderCollapsedChange}
            batchDragActive={batchDragActive}
            dragPayloadIds={dragPayloadIds}
            nodesById={nodesById}
            onKeyboardMove={onKeyboardMove}
            ancestorSelected={isVisuallySelected}
          />
          {showFolderEndDroppable && (
            <FolderEndDroppableArea
              folderId={node.id}
              nestingLevel={nestingLevel + 1}
              displayOrder={node.children.length > 0 ? Math.max(...node.children.map((child) => child.displayOrder)) + 1 : 1}
              isDragging={draggingNode !== null && draggingNode.id !== node.id && !node.children.map((child) => child.id).includes(draggingNode.id)}
              batchDragActive={batchDragActive}
              dragPayloadIds={dragPayloadIds}
              nodesById={nodesById}
            />
          )}
        </>
      )}
    </Fragment>
  );
};

const TreeDisplay = memo(function TreeDisplay({
  tree,
  getDocument,
  getFolder,
  parentId,
  newTitle,
  setNewTitle,
  updateTitle,
  updateDocumentTitle,
  inputRef,
  nestingLevel,
  draggingNode,
  selectedNodeIds,
  onNodeSelect,
  collapsedFolderIds,
  keyboardExpandedFolderIds,
  clearKeyboardExpandedFolderId,
  onFolderCollapsedChange,
  batchDragActive,
  dragPayloadIds,
  nodesById,
  onKeyboardMove,
  ancestorSelected = false,
}) {
  if (!nestingLevel) nestingLevel = 0;

  if (tree.length === 0) return null;

  let previousDisplayOrder = firstDisplayOrder(tree);

  return (
    <ul
      className="tree-display"
      style={{ position: "relative" }}
      role="group"
    >
      {tree.map((node) => {
        let displayOrder = (previousDisplayOrder + node.displayOrder) / 2;

        previousDisplayOrder = node.displayOrder;

        if (node.type === "document") {
          const item = getDocument(node.id);
          // hacky fix around delete item optimistic updating
          if (!item) return null;

          return (
            <Fragment key={node.id}>
              <DroppableArea
                parentId={node.parentId}
                draggingNode={draggingNode}
                nestingLevel={nestingLevel}
                displayOrder={displayOrder}
                dragPayloadIds={dragPayloadIds}
                nodesById={nodesById}
              />
              <SortableDocument
                key={item.id}
                item={item}
                newTitle={newTitle}
                setNewTitle={setNewTitle}
                updateTitle={updateTitle}
                updateDocumentTitle={updateDocumentTitle}
                inputRef={inputRef}
                nestingLevel={nestingLevel}
                isSelected={ancestorSelected || selectedNodeIds.includes(item.id)}
                ariaSelected={selectedNodeIds.includes(item.id)}
                onSelect={onNodeSelect}
                onKeyboardMove={onKeyboardMove}
              />
            </Fragment>
          );
        } else {
          const folder = getFolder(node.id);

          return (
            <TreeDisplayFolder
              key={node.id}
              node={node}
              folder={folder}
              draggingNode={draggingNode}
              nestingLevel={nestingLevel}
              displayOrder={displayOrder}
              inputRef={inputRef}
              getDocument={getDocument}
              getFolder={getFolder}
              newTitle={newTitle}
              setNewTitle={setNewTitle}
              updateDocumentTitle={updateDocumentTitle}
              updateTitle={updateTitle}
              selectedNodeIds={selectedNodeIds}
              onNodeSelect={onNodeSelect}
              collapsedFolderIds={collapsedFolderIds}
              keyboardExpandedFolderIds={keyboardExpandedFolderIds}
              clearKeyboardExpandedFolderId={clearKeyboardExpandedFolderId}
              onFolderCollapsedChange={onFolderCollapsedChange}
              batchDragActive={batchDragActive}
              dragPayloadIds={dragPayloadIds}
              nodesById={nodesById}
              onKeyboardMove={onKeyboardMove}
              ancestorSelected={ancestorSelected}
            />
          );
        }
      })}
      <DroppableArea
        parentId={parentId ?? "none"}
        draggingNode={draggingNode}
        nestingLevel={nestingLevel}
        displayOrder={Math.max(...tree.map((node) => node.displayOrder)) + 1}
        dragPayloadIds={dragPayloadIds}
        nodesById={nodesById}
      />
    </ul>
  );
});

const DraggedNodeOverlay = ({ draggedNode, dragPayloadIds, getDocument, getFolder }) => {
  if (!draggedNode) return <DragOverlay />;
  const displayTitle = draggedNode.type === "document" ? getDocument(draggedNode.id)?.title : getFolder(draggedNode.id)?.name || draggedNode.name;
  const overlayLabel = dragPayloadIds.length > 1 ? `${dragPayloadIds.length} items` : displayTitle;
  const src = draggedNode.type === "document" ? "/assets/document.svg" : "/assets/single-folder.svg";
  // This is a styled wrapper for the InlineSVG component
  const StyledSVG = styled(InlineSVG)`
    & path {
      fill: var(--grey-0); // This sets the SVG fill color to white
    }
    cursor: grab;
    margin-right: 4px;
  `;
  return (
    <DragOverlay>
      <div
        style={{
          color: "var(--grey-0)",
          width: "calc(100% - 10px)",
          opacity: ".8",
          fontWeight: "500",
          borderRadius: "5px",
          backgroundColor: "var(--menu-color)",
          display: "flex",
          alignItems: "center",
          margin: "0px",
          padding: "0px 4px 0px 4px",
        }}
      >
        <StyledSVG
          src={src}
          width={28}
          height={28}
        />
        {overlayLabel}
      </div>
    </DragOverlay>
  );
};

export const DocumentsListSB = ({ contents, newTitle, setNewTitle, updateDocumentTitle, inputRef, updateTitle }) => {
  const [draggingNode, setDraggingNode] = useState(null);
  const [displayTree, setDisplayTree] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectionAnchorId, setSelectionAnchorId] = useState(null);
  const [dragPayloadIds, setDragPayloadIds] = useState([]);
  const [collapsedFolderIds, setCollapsedFolderIds] = useState(() => new Set());
  const [keyboardExpandedFolderIds, setKeyboardExpandedFolderIds] = useState(() => new Set());
  const project = useProjectStore((state) => state.project);
  const projectId = project?.id ?? null;
  /** While true, skip syncing displayTree from project while keyboard saves settle in the background. */
  const suppressProjectTreeSyncRef = useRef(false);

  const revealFolder = useStableCallback((folderId) => {
    setKeyboardExpandedFolderIds((previous) => new Set(previous).add(folderId));
  });

  const { onKeyboardMove, cancelPendingKeyboardSave, announcement, lastMovedNodeIdRef } = useKeyboardReorder({
    project,
    displayTree,
    setDisplayTree,
    suppressProjectTreeSyncRef,
    revealFolder,
  });

  const clearKeyboardExpandedFolderId = useCallback((folderId) => {
    setKeyboardExpandedFolderIds((previous) => {
      const next = new Set(previous);
      next.delete(folderId);
      return next;
    });
  }, []);

  useEffect(() => {
    if (lastMovedNodeIdRef.current) {
      const nodeId = lastMovedNodeIdRef.current;
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-node-id="${nodeId}"]`);
        if (el) el.focus();
        if (!suppressProjectTreeSyncRef.current) {
          lastMovedNodeIdRef.current = null;
        }
      });
    }
  }, [displayTree]);

  const handleFolderCollapsedChange = useCallback((folderId, collapsed) => {
    setCollapsedFolderIds((previous) => {
      const next = new Set(previous);
      if (collapsed) next.add(folderId);
      else next.delete(folderId);
      return next;
    });
  }, []);

  const batchDragActive = dragPayloadIds.length > 1;

  const getDocument = useStableCallback((id) => contents.find((doc) => doc.id === id));
  const getFolder = useStableCallback((id) => project.folders.find((folder) => folder.id === id));

  /** Stable key for when server tree or folder collapsed flags change (avoids effect deps on JSON.stringify(projectTree)). */
  const projectTreeSyncKey = useMemo(() => getProjectTreeSyncKey(project, contents), [project, contents]);

  useEffect(() => {
    if (suppressProjectTreeSyncRef.current) return;
    setDisplayTree(project.getRootTree());
    setCollapsedFolderIds(() => {
      const next = new Set();
      for (const f of project.folders || []) {
        if (f.collapsed) next.add(f.id);
      }
      return next;
    });
  }, [project, projectTreeSyncKey]);

  const visibleNodeIds = useMemo(() => buildVisibleNodeIds(displayTree, [], collapsedFolderIds), [displayTree, collapsedFolderIds]);
  // `project` is a mutable class instance; its reference does not change when tree data updates in place.
  // Depend on a structural signature so nodesById refreshes after moves/fetches.
  const treeStructureSignature = project
    .getAllTreeNodes()
    .map((n) => `${n.id}:${n.parentId}:${n.displayOrder}`)
    .join("|");
  const nodesById = useMemo(
    () => Object.fromEntries(project.getAllTreeNodes().map((node) => [node.id, node])),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- project ref is stable; treeStructureSignature captures tree mutations
    [treeStructureSignature]
  );

  const handleNodeSelect = useCallback(
    (event, nodeId) => {
      const nextSelection = getNextSelection({
        clickedId: nodeId,
        visibleNodeIds,
        selectedIds: selectedNodeIds,
        anchorId: selectionAnchorId,
        isShiftKey: event.shiftKey,
        isMetaKey: event.metaKey || event.ctrlKey,
      });

      const isMultiSelectGesture = event.shiftKey || event.metaKey || event.ctrlKey;
      const isMultiItemSelection = nextSelection.selectedIds.length > 1;
      if (isMultiSelectGesture || isMultiItemSelection) {
        sendAppEvent("Project Tree - Multi Select", {
          selection_type: event.shiftKey ? "range" : event.metaKey || event.ctrlKey ? "individual" : "single",
          selected_count: nextSelection.selectedIds.length,
          previous_count: selectedNodeIds.length,
        });
      }

      setSelectedNodeIds(nextSelection.selectedIds);
      setSelectionAnchorId(nextSelection.anchorId);
      useProjectTreeSelectionStore.getState().setSelectedNodeIds(nextSelection.selectedIds, projectId);
    },
    [projectId, selectionAnchorId, selectedNodeIds, visibleNodeIds],
  );

  const selectedNodeIdsRef = useRef(selectedNodeIds);
  selectedNodeIdsRef.current = selectedNodeIds;
  const selectionAnchorIdRef = useRef(selectionAnchorId);
  selectionAnchorIdRef.current = selectionAnchorId;

  const clearSelection = useCallback(() => {
    // Skip no-op clears (e.g. editor click-away) so the tree does not re-render.
    if (selectedNodeIdsRef.current.length === 0 && selectionAnchorIdRef.current === null) return;
    setSelectedNodeIds([]);
    setSelectionAnchorId(null);
    useProjectTreeSelectionStore.getState().clearSelectedNodeIds(projectId);
  }, [projectId]);

  const clearSelectionRef = useRef(clearSelection);
  clearSelectionRef.current = clearSelection;

  useEffect(() => {
    clearSelection();
    return () => {
      useProjectTreeSelectionStore.getState().clearSelectedNodeIds(projectId);
    };
  }, [projectId, clearSelection]);

  useEffect(() => {
    const nextSelection = pruneSelectionToVisible({
      selectedIds: selectedNodeIds,
      anchorId: selectionAnchorId,
      visibleNodeIds,
    });

    if (nextSelection.selectedIds.length !== selectedNodeIds.length || nextSelection.anchorId !== selectionAnchorId) {
      setSelectedNodeIds(nextSelection.selectedIds);
      setSelectionAnchorId(nextSelection.anchorId);
      useProjectTreeSelectionStore.getState().setSelectedNodeIds(nextSelection.selectedIds, projectId);
    }
  }, [projectId, selectionAnchorId, selectedNodeIds, visibleNodeIds]);

  useEffect(() => {
    const handleDocumentClickAway = (event) => {
      if (!shouldPreserveProjectTreeSelection(event.target)) {
        clearSelectionRef.current();
      }
    };

    document.addEventListener("click", handleDocumentClickAway);
    return () => document.removeEventListener("click", handleDocumentClickAway);
  }, []);

  const persistBatchTreeMove = useCallback(
    async (movePlan) => {
      const nextProjectState = buildBatchProjectUpdate({
        folders: project.folders || [],
        contents: project.data.contents.contents,
        movePlan,
      });

      await project.update({
        folders: nextProjectState.folders,
        contents: {
          ...project.data.contents,
          contents: nextProjectState.contents,
        },
      });
    },
    [project],
  );

  const runBatchTreeMove = async (movePlan, analyticsParentId, selectedIds, analyticsNodesById = nodesById) => {
    if (movePlan.length === 0) return;

    cancelPendingKeyboardSave();
    suppressProjectTreeSyncRef.current = true;
    try {
      setDisplayTree(applyBatchTreeMoves(project.getAllTreeNodes(), movePlan));
      await persistBatchTreeMove(movePlan);
      setDisplayTree(project.getRootTree());
      sendAppEvent("Project Tree - Batch Move Completed", {
        item_count: getBatchMoveItemCount(selectedIds, analyticsNodesById),
        destination_folder_id: analyticsParentId,
        destination_type: !analyticsParentId || analyticsParentId === "none" ? "root" : "folder",
      });
    } catch (error) {
      await project.fetch();
      setDisplayTree(project.getRootTree());
    } finally {
      suppressProjectTreeSyncRef.current = false;
    }
  };

  const reorderDocuments = async (draggingNode, target) => {
    if (!draggingNode) return;

    const { displayOrder, parentId } = target;
    const selectedIds = dragPayloadIds.length > 0 ? dragPayloadIds : [draggingNode.id];
    const pendingKeyboardState = cancelPendingKeyboardSave();
    let activeNodesById = nodesById;

    if (pendingKeyboardState) {
      project.mergeData(pendingKeyboardState.projectUpdate);
      project.onChange?.(project);
      activeNodesById = Object.fromEntries(pendingKeyboardState.nodes.map((node) => [node.id, node]));
    }

    if (selectedIds.length > 1 && isBatchFolderDropTarget(target.dropType)) {
      const isFolderDrop = target.dropType === "folder";
      const isSeparatorDrop = target.dropType === "separator";
      const isFolderEndDrop = target.dropType === "folder-end";

      const validation = validateBatchFolderDrop({
        selectedIds,
        destinationFolderId: parentId,
        nodesById: activeNodesById,
      });

      if (!validation.valid) {
        return;
      }

      let movePlan;
      if (isFolderDrop) {
        movePlan = buildBatchFolderMovePlan({
          selectedIds,
          visibleNodeIds,
          destinationFolderId: parentId,
          nodesById: activeNodesById,
        });
        await runBatchTreeMove(movePlan, parentId, selectedIds, activeNodesById);
        return;
      }

      if (!isSeparatorDrop && !isFolderEndDrop) {
        return;
      }

      const insertBefore = isFolderEndDrop ? Number.POSITIVE_INFINITY : displayOrder;
      movePlan = buildBatchSeparatorMovePlan({
        selectedIds,
        visibleNodeIds,
        destinationParentId: parentId,
        insertBeforeDisplayOrder: insertBefore,
        nodesById: activeNodesById,
      });
      await runBatchTreeMove(movePlan, parentId, selectedIds, activeNodesById);
      return;
    }

    const update = { displayOrder, parentId };
    const movePlan = [{ id: draggingNode.id, ...update }];
    const optimizedTree = pendingKeyboardState
      ? applyBatchTreeMoves(project.getAllTreeNodes(), movePlan)
      : project.manipulateTree(draggingNode.id, update);
    setDisplayTree(optimizedTree);
    suppressProjectTreeSyncRef.current = true;
    try {
      if (pendingKeyboardState) {
        await persistBatchTreeMove(movePlan);
      } else {
        await project.updateTree(draggingNode.id, update);
      }
      setDisplayTree(project.getRootTree());
    } catch (error) {
      await project.fetch();
      setDisplayTree(project.getRootTree());
    } finally {
      suppressProjectTreeSyncRef.current = false;
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));

  const handleDragStart = useStableCallback(({ active }) => {
    if (!active) return;
    const node = project.getAllTreeNodes().find((node) => node.id === active.id);
    const nextDragPayloadIds = selectedNodeIds.includes(active.id) ? selectedNodeIds : [active.id];

    if (!selectedNodeIds.includes(active.id)) {
      setSelectedNodeIds([active.id]);
      setSelectionAnchorId(active.id);
      useProjectTreeSelectionStore.getState().setSelectedNodeIds([active.id], projectId);
    }

    if (nextDragPayloadIds.length > 1) {
      sendAppEvent("Project Tree - Batch Move Started", {
        item_count: nextDragPayloadIds.length,
        item_types: nextDragPayloadIds.map((id) => nodesById[id]?.type).filter(Boolean),
        source: "multi_select_drag",
      });
    }

    setDraggingNode(node);
    setDragPayloadIds(nextDragPayloadIds);
  });

  const handleDragEnd = useStableCallback(async ({ over }) => {
    const currentDraggingNode = draggingNode;
    setDraggingNode(null);
    setDragPayloadIds([]);
    if (over) {
      const target = {
        // mapping for easier use
        displayOrder: over.data?.current?.displayOrder,
        dropType: over.data?.current?.dropType,
        parentId: over.data?.current?.parentId,
      };

      await reorderDocuments(currentDraggingNode, target);
    }
  });

  const handleDragCancel = useStableCallback(() => {
    setDraggingNode(null);
    setDragPayloadIds([]);
  });

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={customCollision}
      modifiers={DND_MODIFIERS}
    >
      <div
        id="draggable-root"
        role="tree"
        aria-label="Document list"
        className="mt-1"
      >
        <div aria-live="polite" aria-atomic="true" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
          {announcement}
        </div>
        <TreeDisplay
          tree={displayTree}
          getDocument={getDocument}
          getFolder={getFolder}
          parentId={undefined}
          newTitle={newTitle}
          setNewTitle={setNewTitle}
          updateDocumentTitle={updateDocumentTitle}
          inputRef={inputRef}
          updateTitle={updateTitle}
          draggingNode={draggingNode}
          selectedNodeIds={selectedNodeIds}
          onNodeSelect={handleNodeSelect}
          collapsedFolderIds={collapsedFolderIds}
          keyboardExpandedFolderIds={keyboardExpandedFolderIds}
          clearKeyboardExpandedFolderId={clearKeyboardExpandedFolderId}
          onFolderCollapsedChange={handleFolderCollapsedChange}
          batchDragActive={batchDragActive}
          dragPayloadIds={dragPayloadIds}
          nodesById={nodesById}
          onKeyboardMove={onKeyboardMove}
        />
      </div>
      <DraggedNodeOverlay
        draggedNode={draggingNode}
        dragPayloadIds={dragPayloadIds}
        getDocument={getDocument}
        getFolder={getFolder}
      />
    </DndContext>
  );
};
